import {useRef} from "react";
import {useMessages} from "../i18n";
import {useIsomorphicLayoutEffect} from "../utils/useIsomorphicLayoutEffect";
import {FOOTER_LEGAL_LINKS} from "./resourceNavigation";
import {FooterLink, Links, Root, SITE_FOOTER_RESERVE_PROPERTY} from "./SiteFooter.styles";

/**
 * The site-wide legal footer (kitecorp/transferify-ui#2394, owner decision 2026-09-18).
 *
 * <p><b>Why it exists at all.</b> ui#2327 put the statutory cancellation control
 * („Verträge hier kündigen“) in the header's Support disclosure. § 312k Abs. 2 S. 4 BGB requires that
 * control to be "ständig verfügbar sowie unmittelbar und leicht zugänglich", and two independent legal
 * passes read a control that only exists after a visitor opens a collapsed dropdown as arguable against
 * that standard (OLG München 20.03.2025, 6 U 4336/23 e; LG München I 16.11.2023, 12 O 4127/23; BGH
 * 16.07.2026, I ZR 200/25). A permanently visible page-level link is the safe reading, so the dropdown
 * entry STAYS and this row is the second, always-rendered control.
 *
 * <p><b>The row is short on purpose.</b> LG München I 3 HK O 13796/24 held that a cancellation control
 * buried among many footer links is not "leicht zugänglich" either, so option (b) meant a short legal
 * row — cancellation, AGB, Datenschutz, Impressum — and not a link farm. A fifth destination here is
 * therefore a legal decision as well as a layout one, and it failed on both counts: the Advertising
 * Terms (kitecorp/transferify-ui#2602) stayed out because a fifth label wraps the row at 375px and the
 * create card loses that height. See `FOOTER_LEGAL_LINKS` for the measurement.
 *
 * <p><b>Every brand, one implementation.</b> § 312k is German law and only the German storefront is
 * required to carry this, but each locale already owns the label
 * (`nav.resourcesMenu.cancelContract`: "Cancel contracts" / „Anulează contractele“ /
 * „Verträge hier kündigen“), and a legal row beside the AGB is ordinary on the other two. One footer
 * for all three is the simplest thing that satisfies the requirement and harms nothing.
 *
 * <p><b>It must work before a line of JavaScript runs.</b> Each row is a real anchor with a
 * root-relative `href` in the pre-rendered HTML, so a consumer whose script never loads still has a
 * working control — which is the whole difference between this and the disclosure, whose rows are laid
 * out but `visibility: hidden` until the panel is opened. The seam's `SiteLink` wraps `react-router`'s
 * `Link`, which emits exactly that anchor and adds the client-side transition once hydrated; neither is
 * what makes the link clickable.
 *
 * <p>The hrefs go through the site-link seam ({@link ./SiteLink}), which `FooterLink` is styled from,
 * so they carry both of the rules a site link obeys. The HOST rule (ui#432) is why they were absolute
 * already: the ONE pre-rendered shell is also served on a customer's claimed subdomain, where the edge
 * serves recipient surfaces only and every root-relative site link is a 404. The TREE rule (ui#2498) is
 * why the seam replaced the direct `siteLinkHref` call: on `transferify.cloud/de/**` this row used to be
 * four links out of German, which is the worst place to lose the language — the § 312k control is the
 * one link here a consumer is entitled to find in their own. On every brand host and every development
 * host both rules answer "no signal" and the href is byte-identical to the path.
 *
 * <p>No `<nav>` landmark and no `aria-label`: a `<footer>` that is not inside `article`/`aside`/`main`/
 * `nav`/`section` already IS the document's `contentinfo` landmark, and a second navigation landmark
 * would need a name of its own purely to keep `landmark-unique` happy. The row carries no copy beyond
 * the link labels it reuses, so it adds no catalogue key.
 */
export function SiteFooter() {
	const {nav} = useMessages();
	const root = useRef<HTMLElement>(null);
	/**
	 * How much of the window this row takes, published for the boxes that size themselves against the
	 * window and are laid out ABOVE it (kitecorp/transferify-ui#2631).
	 *
	 * <p>The row is in the app scroller's flow, so every single-screen surface above it now ends one
	 * row short of the window. A box that still budgets against `100dvh` overruns that end by exactly
	 * this much — measured at 1280x700, the create card's own cap left its gift tag hanging 20px past
	 * the landing surface, which clips it, so a third of a 44px control was not on screen.
	 *
	 * <p>MEASURED rather than stated: the row is four 44px touch targets and it wraps, so its height is
	 * 65px on one row and 115px on the two the narrowest phone needs — and a locale with longer labels
	 * wraps at a different width again. A constant here would be right on one phone and wrong on the
	 * next.
	 *
	 * <p>Cleared on unmount, so the routes `nav/siteFooterRoutes` keeps the row off (the viewport-locked
	 * delivery pages) reserve nothing: the property simply goes back to absent and every reader's own
	 * `0px` fallback applies.
	 */
	useIsomorphicLayoutEffect(() => {
		const element = root.current;
		if (element === null) return undefined;
		const documentRoot = document.documentElement;
		const publish = () => documentRoot.style.setProperty(SITE_FOOTER_RESERVE_PROPERTY,
			`${element.getBoundingClientRect().height}px`);
		publish();
		// The row's height changes with WRAPPING, so the trigger is its own box rather than a resize
		// event: the fonts settling re-wraps it too, and nothing tells the window about that.
		const observer = new ResizeObserver(publish);
		observer.observe(element);
		return () => {
			observer.disconnect();
			documentRoot.style.removeProperty(SITE_FOOTER_RESERVE_PROPERTY);
		};
	}, []);
	return (
		<Root ref={root}>
			<Links>
				{FOOTER_LEGAL_LINKS.map((item) => (
					<li key={item.to}>
						<FooterLink to={item.to}>
							{nav.resourcesMenu[item.labelKey]}
						</FooterLink>
					</li>
				))}
			</Links>
		</Root>
	);
}
